import { useEffect, useRef, useCallback } from "react";

const SUPABASE_URL = import.meta.env.VITE_PUBLIC_SUPABASE_URL as string;
const SUPABASE_ANON_KEY = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY as string;

const POLL_INTERVAL = 30000; // 30 seconds

interface AutoReviewResult {
  carrier: string;
  status: "success" | "spam";
}

export function useCarrierTestAutoReview(
  hasPendingReviews: boolean,
  onReviewed: () => void,
) {
  const processingRef = useRef(false);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const runAutoReview = useCallback(async () => {
    if (processingRef.current) return;
    processingRef.current = true;

    try {
      const resp = await fetch(`${SUPABASE_URL}/functions/v1/auto-review-carrier-tests`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          apikey: SUPABASE_ANON_KEY,
        },
      });

      if (!resp.ok) return;

      const data = await resp.json();
      if (data.processed > 0) {
        console.log(
          `[CarrierAutoReview] Auto-reviewed ${data.processed} test(s):`,
          data.details
        );
        onReviewed();
      }
    } catch (err) {
      console.warn("[CarrierAutoReview] Poll failed:", err);
    } finally {
      processingRef.current = false;
    }
  }, [onReviewed]);

  useEffect(() => {
    if (!hasPendingReviews) {
      // Clear any existing timer when no pending reviews
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      return;
    }

    // Initial delay of 2 seconds, then every POLL_INTERVAL
    const initialTimer = setTimeout(() => {
      runAutoReview();
      timerRef.current = setInterval(runAutoReview, POLL_INTERVAL);
    }, 2000);

    return () => {
      clearTimeout(initialTimer);
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [hasPendingReviews, runAutoReview]);

  return { triggerNow: runAutoReview };
}